import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { collection, getDocs, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/fbConfig';
import { syncSubjectsWithStudents } from './SyncSubjects';

const PromoteClassModal = ({ isOpen, onClose, classId, currentClass, students, subjects, onSuccess }) => {
  const [sessions, setSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [className, setClassName] = useState(currentClass?.name || '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch sessions
        const sessionsSnap = await getDocs(collection(db, 'sessions'));
        const sessionsData = sessionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSessions(sessionsData);

        // Fetch terms
        const termsSnap = await getDocs(collection(db, 'terms'));
        const termsData = termsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTerms(termsData);
      } catch (error) {
        console.error("Error fetching sessions or terms:", error);
      }
    };

    if (isOpen) {
      fetchData();
      setClassName(currentClass?.name || '');
    }
  }, [isOpen, currentClass]);

  const handlePromote = async () => {
    if (!selectedSession || !selectedTerm || !className) {
      alert('Please select a session, term, and provide a class name.');
      return;
    }

    setLoading(true);
    try {
      // Create new class
      const newClassData = {
        name: className,
        section: currentClass.sectionId || '', // Copy the current class's section ID
        session: selectedSession,
        term: selectedTerm,
        formMaster: currentClass.formMaster?.id || '',
        students: students.map(student => student.id),
        subjects: subjects.map(subject => ({ id: subject.id, teacherId: '' })),
      };

      const newClassRef = await addDoc(collection(db, 'classes'), newClassData);
      const newClassId = newClassRef.id;

      // Update student enrollments
      const psychomotorSkills = {
        handwriting: 'A',
        fluency: 'A',
        games: 'A',
        punctuality: 'A',
        neatness: 'A',
        politeness: 'A',
        cooperation: 'A',
        leadership: 'A',
        attentiveness: 'A',
        honesty: 'A',
      };

      await Promise.all(
        students.map(async (student) => {
          const studentRef = doc(db, 'students', student.id);
          const studentDoc = await getDoc(studentRef);
          const studentData = studentDoc.data();

          const subjectsWithScores = subjects.map(subject => ({
            subjectId: subject.id,
            teacherId: '',
            scores: {
              firstca: 0,
              secondca: 0,
              exam: 0,
            },
          }));

          const updatedEnrollments = [
            ...(studentData.enrollments || []),
            {
              classId: newClassId,
              subjects: subjectsWithScores,
              psychomotorSkills,
              fees: 0,
              paid: 0,
              Attendance: [], // Initialize empty Attendance array
            },
          ];

          await updateDoc(studentRef, { enrollments: updatedEnrollments });
        })
      );

      // Sync subjects with students
      await syncSubjectsWithStudents(newClassId);

      onSuccess();
    } catch (error) {
      console.error("Error promoting class:", error);
      alert('Failed to promote class. Please try again.');
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Promote Class"
      className="modal"
      overlayClassName="modal-overlay"
    >
      <div className="modal-content">
        <h2>Promote Class: {currentClass?.name}</h2>
        <div className="modal-form">
          <label className="text-left">Class Name</label>
          <input
            type="text"
            className="input-field"
            placeholder="Enter new class name"
            value={className}
            onChange={(e) => setClassName(e.target.value)}
          />
          <label className="text-left">Select Session</label>
          <select
            className="input-field"
            value={selectedSession}
            onChange={(e) => setSelectedSession(e.target.value)}
          >
            <option value="">Select a session</option>
            {sessions.map(session => (
              <option key={session.id} value={session.id}>
                {session.name}
              </option>
            ))}
          </select>
          <label className="text-left">Select Term</label>
          <select
            className="input-field"
            value={selectedTerm}
            onChange={(e) => setSelectedTerm(e.target.value)}
          >
            <option value="">Select a term</option>
            {terms.map(term => (
              <option key={term.id} value={term.id}>
                {term.name}
              </option>
            ))}
          </select>
          <div className="row align-center justify-center gap-15">
            <button
              className="login-button"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="login-button"
              onClick={handlePromote}
              disabled={loading}
            >
              {loading ? 'Promoting...' : 'Promote Class'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PromoteClassModal;